import {
  type ConditionExpressionType,
  conditionShapeForDataType,
} from "@redotech/redo-flows-core-schema-common/type-system/conditionable-field";
import type { GenericDataType } from "@redotech/redo-flows-core-schema-common/type-system/data-types/generic-types";
import { variableDisplayName } from "@redotech/redo-flows-core-schema-common/type-system/variable-display";
import * as z from "zod";
import {
  arrayPredicateExpressionSchema_,
  booleanEvaluationExpressionSchema_,
  checkoutContextPredicateExpressionSchema_,
  numberComparisonExpressionSchema_,
  schemaBooleanExpressionSchema,
  type SchemaBooleanExpressionType,
  textMatchExpressionSchema_,
} from "./advanced-flow-db-parser";
import { schemas, type SchemaType } from "./schemas/schemas";

/** Recover reads offer state the public API cannot write, so it stays out. */
export const FORBIDDEN_CHECKOUT_PREDICATE_TYPES: ReadonlySet<string> = new Set([
  "recover_discount_eligibility",
]);

const publicCheckoutPredicateOptions =
  checkoutContextPredicateExpressionSchema_.options.filter(
    (option) =>
      !FORBIDDEN_CHECKOUT_PREDICATE_TYPES.has(option.shape.predicateType.value),
  );

export const publicCheckoutPredicateTypes: readonly string[] =
  publicCheckoutPredicateOptions.map(
    (option) => option.shape.predicateType.value,
  );

export type TriggerPredicateExpressionType =
  | ConditionExpressionType
  | "array_predicate";

/** Stored `operator` values, read off the validator so the two cannot drift. */
const operatorsByExpressionType: Record<
  TriggerPredicateExpressionType,
  readonly string[]
> = {
  text_match: textMatchExpressionSchema_.shape.operator.options,
  number_comparison: numberComparisonExpressionSchema_.shape.operator.options,
  boolean_evaluation: [],
  checkout_context_predicate: [],
  array_predicate: arrayPredicateExpressionSchema_.shape.operator.options,
};

export interface TriggerConditionElement {
  expressionType: ConditionExpressionType;
  operators: readonly string[];
}

/** One trigger-payload field a trigger-data predicate can name. */
export interface TriggerConditionField {
  path: string;
  displayName: string;
  description: string;
  dataType: GenericDataType;
  expressionType: TriggerPredicateExpressionType;
  operators: readonly string[];
  /** Set for `array_predicate`: the expression its `condition` must be. */
  element: TriggerConditionElement | null;
  enumValues: readonly string[] | null;
}

interface FieldArm {
  field: TriggerConditionField;
  schema: z.ZodType;
}

function scalarSchema(
  path: string,
  expressionType: ConditionExpressionType,
): z.ZodType {
  const field = z.literal(path);
  switch (expressionType) {
    case "text_match":
      return textMatchExpressionSchema_.extend({ field });
    case "number_comparison":
      return numberComparisonExpressionSchema_.extend({ field });
    case "boolean_evaluation":
      return booleanEvaluationExpressionSchema_.extend({ field });
    case "checkout_context_predicate":
      return z.union(
        publicCheckoutPredicateOptions.map((option) =>
          option.extend({ field }),
        ),
      );
  }
}

/**
 * The builder's trigger-data fields. Array elements reuse the array's path, as
 * the builder writes them, and checkout predicates cannot be nested.
 */
function fieldArms(schemaType: SchemaType): FieldArm[] {
  const arms: FieldArm[] = [];
  for (const [path, definition] of Object.entries(schemas[schemaType].fields)) {
    if (definition.hideFromConditionStep) {
      continue;
    }
    const shape = conditionShapeForDataType(definition.dataType);
    if (shape == null) {
      continue;
    }
    const base = {
      path,
      displayName: variableDisplayName({ ...definition, key: path }),
      description: definition.documentation,
      dataType: definition.dataType,
      enumValues: definition.values ?? null,
    };
    if (shape.kind === "scalar") {
      arms.push({
        field: {
          ...base,
          expressionType: shape.expressionType,
          operators: operatorsByExpressionType[shape.expressionType],
          element: null,
        },
        schema: scalarSchema(path, shape.expressionType),
      });
      continue;
    }
    const element = shape.element;
    if (
      element.kind !== "scalar" ||
      element.expressionType === "checkout_context_predicate"
    ) {
      continue;
    }
    arms.push({
      field: {
        ...base,
        expressionType: "array_predicate",
        operators: operatorsByExpressionType.array_predicate,
        element: {
          expressionType: element.expressionType,
          operators: operatorsByExpressionType[element.expressionType],
        },
      },
      schema: arrayPredicateExpressionSchema_.extend({
        field: z.literal(path),
        condition: scalarSchema(path, element.expressionType),
      }),
    });
  }
  return arms;
}

const armsBySchemaType = new Map<SchemaType, FieldArm[]>();

function armsFor(schemaType: SchemaType): FieldArm[] {
  const cached = armsBySchemaType.get(schemaType);
  if (cached) {
    return cached;
  }
  const arms = fieldArms(schemaType);
  armsBySchemaType.set(schemaType, arms);
  return arms;
}

export function triggerConditionFields(
  schemaType: SchemaType,
): TriggerConditionField[] {
  return armsFor(schemaType).map((arm) => arm.field);
}

/** Every predicate the public API accepts on this trigger: one arm per field. */
export function publicTriggerPredicateSchema(
  schemaType: SchemaType,
): z.ZodType {
  const arms = armsFor(schemaType);
  return arms.length === 0 ? z.never() : z.union(arms.map((arm) => arm.schema));
}

export function publicTriggerPredicateJsonSchema(
  schemaType: SchemaType,
): Record<string, unknown> {
  return z.toJSONSchema(publicTriggerPredicateSchema(schemaType), {
    io: "input",
    unrepresentable: "any",
  });
}

export function formatZodIssues(label: string, error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const path = [label, ...issue.path.map(String)].join(".");
      const expected =
        issue.code === "invalid_value"
          ? ` (expected one of ${issue.values.map(String).join(", ")})`
          : "";
      return `${path}: ${issue.message}${expected}`;
    })
    .join("; ");
}

export type PublicTriggerPredicateResult =
  | { success: true; data: SchemaBooleanExpressionType }
  | { success: false; message: string };

function rawField(value: unknown): unknown {
  return value != null && typeof value === "object" && "field" in value
    ? value.field
    : undefined;
}

/** The stored parse normalizes legacy spellings and is what gets stored; the field's arm gives the error path. */
export function parsePublicTriggerPredicate(
  schemaType: SchemaType,
  value: unknown,
  label: string,
): PublicTriggerPredicateResult {
  const stored = schemaBooleanExpressionSchema.safeParse(value);
  const field = stored.success ? stored.data.field : rawField(value);
  const arms = armsFor(schemaType);
  const arm = arms.find((candidate) => candidate.field.path === field);
  if (arm == null) {
    const known = arms.map((candidate) => candidate.field.path).join(", ");
    return {
      success: false,
      message: `${label}.field: ${String(field)} is not a condition field of this trigger (expected one of ${known || "none"}; see marketingAutomationCatalog triggerDataFields)`,
    };
  }
  const checked = arm.schema.safeParse(stored.success ? stored.data : value);
  if (!checked.success) {
    return { success: false, message: formatZodIssues(label, checked.error) };
  }
  if (!stored.success) {
    return { success: false, message: formatZodIssues(label, stored.error) };
  }
  return { success: true, data: stored.data };
}
